import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

export interface RestaurantBanner {
  id: string;
  restaurant_id: string;
  title: string | null;
  subtitle: string | null;
  image_url: string;
  cta_text: string;
  link_type: string;
  link_target_id: string | null;
  display_order: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface PublicBanner {
  id: string;
  title: string | null;
  subtitle: string | null;
  image_url: string;
  cta_text: string;
  link_type: string;
  link_target_id: string | null;
  display_order: number;
}

export function useRestaurantBanners(restaurantId: string | undefined) {
  const [banners, setBanners] = useState<RestaurantBanner[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const fetchBanners = async () => {
    if (!restaurantId) {
      setIsLoading(false);
      return;
    }

    try {
      const { data, error } = await supabase
        .from("restaurant_banners")
        .select("*")
        .eq("restaurant_id", restaurantId)
        .order("display_order", { ascending: true });

      if (error) throw error;
      setBanners(data || []);
    } catch (err) {
      console.error("Erro ao buscar banners:", err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchBanners();
  }, [restaurantId]);

  const createBanner = async (data: {
    image_url: string;
    title?: string;
    subtitle?: string;
    cta_text?: string;
    link_type?: string;
    link_target_id?: string;
  }) => {
    if (!restaurantId) return false;

    try {
      const maxOrder = banners.length > 0 
        ? Math.max(...banners.map(b => b.display_order)) + 1 
        : 0;

      const { error } = await supabase.from("restaurant_banners").insert({
        restaurant_id: restaurantId,
        image_url: data.image_url,
        title: data.title || null,
        subtitle: data.subtitle || null,
        cta_text: data.cta_text || "PEÇA JÁ",
        link_type: data.link_type || "none",
        link_target_id: data.link_target_id || null,
        display_order: maxOrder,
      });

      if (error) throw error;

      toast.success("Banner criado com sucesso");
      await fetchBanners();
      return true;
    } catch (err) {
      console.error("Erro ao criar banner:", err);
      toast.error("Erro ao criar banner");
      return false;
    }
  };

  const updateBanner = async (id: string, data: Partial<RestaurantBanner>) => {
    try {
      const { error } = await supabase
        .from("restaurant_banners")
        .update(data)
        .eq("id", id);

      if (error) throw error;

      toast.success("Banner atualizado");
      await fetchBanners();
      return true;
    } catch (err) {
      console.error("Erro ao atualizar banner:", err);
      toast.error("Erro ao atualizar banner");
      return false;
    }
  };

  const deleteBanner = async (id: string) => {
    try {
      const { error } = await supabase
        .from("restaurant_banners")
        .delete()
        .eq("id", id);

      if (error) throw error;

      toast.success("Banner excluído");
      await fetchBanners();
      return true;
    } catch (err) {
      console.error("Erro ao excluir banner:", err);
      toast.error("Erro ao excluir banner");
      return false;
    }
  };

  const reorderBanners = async (orderedIds: string[]) => {
    try {
      const updates = orderedIds.map((id, index) => 
        supabase
          .from("restaurant_banners")
          .update({ display_order: index })
          .eq("id", id)
      );

      await Promise.all(updates);
      await fetchBanners();
      return true;
    } catch (err) {
      console.error("Erro ao reordenar banners:", err);
      return false;
    }
  };

  return {
    banners,
    isLoading,
    createBanner,
    updateBanner,
    deleteBanner,
    reorderBanners,
    refetch: fetchBanners,
  };
}

// Hook para buscar banners públicos (sem autenticação)
export function usePublicBanners(restaurantId: string | undefined) {
  const [banners, setBanners] = useState<PublicBanner[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    async function fetchBanners() {
      if (!restaurantId) {
        setIsLoading(false);
        return;
      }

      try {
        const { data, error } = await supabase.rpc("get_public_banners", {
          p_restaurant_id: restaurantId,
        });

        if (error) throw error;
        setBanners((data as PublicBanner[]) || []);
      } catch (err) {
        console.error("Erro ao buscar banners públicos:", err);
      } finally {
        setIsLoading(false);
      }
    }

    fetchBanners();
  }, [restaurantId]);

  return { banners, isLoading };
}
